import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { listCustomers } from '../api/endpoints/customers';
import type { BusinessCustomerSummary, CustomerProfile } from '../api/schemas';
import { ClientCard } from '../components/ClientCard';
import { CustomerDrawer } from '../components/CustomerDrawer';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import {
  customerStatus,
  initialOf,
  matchesQuery,
  monthYear,
  relativeVisit,
  segmentCustomers,
  sortCustomers,
  type CustomerSegment,
  type CustomerSort,
} from '../lib/customers';
import {
  LEVEL_FILTERS,
  LEVEL_LABEL,
  fileVocabulary,
  matchesLevel,
  pausedCustomerIds,
  type LevelFilter,
} from '../lib/customerFile';
import { useBusiness } from '../lib/useBusiness';
import { useCustomerProfiles } from '../lib/useCustomerProfiles';

const PAGE_SIZE = 100;
/* ponytail: everything is client-side under this cap; server-side search/sort
   only if a business ever exceeds it. */
const MAX_AUTO_PAGES = 5;

const SEGMENTS: Array<{ id: CustomerSegment; label: string }> = [
  { id: 'todos', label: 'Todos' },
  { id: 'con-cita', label: 'Con cita' },
  { id: 'nuevos', label: 'Nuevos' },
  { id: 'regulares', label: 'Regulares' },
  { id: 'inactivos', label: 'Inactivos' },
  { id: 'en-pausa', label: 'En pausa' },
];

/** Inteligencia's win-back link opens exactly the customers it counted, under their own chip. */
const PICKED_SEGMENT = { id: 'recuperar', label: 'Para recuperar' } as const;
type Segment = CustomerSegment | typeof PICKED_SEGMENT.id;

const SEGMENT_EMPTY_COPY: Record<Segment, string> = {
  recuperar: 'Esos clientes ya no están en tu lista.',
  todos: 'Ningún cliente coincide con la búsqueda.',
  'con-cita': 'Nadie tiene una cita próxima.',
  nuevos: 'Ningún cliente nuevo en los últimos 30 días.',
  regulares: 'Todavía nadie llega a cinco visitas.',
  inactivos: 'Ningún cliente lleva más de 60 días sin venir.',
  'en-pausa': 'Nadie está en pausa hoy.',
};

type ClientView = 'tarjetas' | 'lista';

function inSegment(
  customers: BusinessCustomerSummary[],
  segment: Segment,
  picked: ReadonlySet<string> | undefined,
  now: Date,
  paused: ReadonlySet<string>,
): BusinessCustomerSummary[] {
  if (segment !== PICKED_SEGMENT.id) return segmentCustomers(customers, segment, now, paused);
  return picked ? customers.filter((customer) => picked.has(customer.customerId)) : [];
}

const CLIENT_VIEWS: readonly { id: ClientView; label: string }[] = [
  { id: 'tarjetas', label: 'Tarjetas' },
  { id: 'lista', label: 'Lista' },
];

export function ClientesPage({ initialQuery, initialIds }: { initialQuery?: string; initialIds?: string[] } = {}) {
  const [query, setQuery] = useState(initialQuery ?? '');
  const [view, setView] = useState<ClientView>('tarjetas');
  const picked = useMemo(() => (initialIds?.length ? new Set(initialIds) : undefined), [initialIds]);
  const [segment, setSegment] = useState<Segment>(picked ? PICKED_SEGMENT.id : 'todos');
  const segments = picked ? [PICKED_SEGMENT, ...SEGMENTS] : SEGMENTS;
  const [sort, setSort] = useState<CustomerSort>('reciente');
  const [selected, setSelected] = useState<BusinessCustomerSummary | null>(null);
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('todos');
  const business = useBusiness();
  const graded = fileVocabulary(business?.category).levels;
  const profiles = useCustomerProfiles();

  const customersQuery = useInfiniteQuery({
    queryKey: ['business-customers'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listCustomers(pageParam, PAGE_SIZE),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  // Pull the whole base up front so search, chips, and figures tell the truth.
  const pages = customersQuery.data?.pages.length ?? 0;
  useEffect(() => {
    if (customersQuery.hasNextPage && !customersQuery.isFetching && !customersQuery.isError && pages < MAX_AUTO_PAGES) {
      void customersQuery.fetchNextPage();
    }
  }, [customersQuery, pages]);

  const customers = customersQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const now = new Date();
  // A trip the team marked is not silence: paused today reads En pausa, never Inactivo.
  const paused = pausedCustomerIds(profiles, now);
  const visible = sortCustomers(
    inSegment(customers, segment, picked, now, paused).filter(
      (c) => matchesQuery(c, query) && (!graded || matchesLevel(profiles.get(c.customerId), levelFilter)),
    ),
    sort,
  );

  const figures = [
    { label: 'Clientes', value: customers.length },
    { label: 'Con cita', value: segmentCustomers(customers, 'con-cita', now).length },
    { label: 'Nuevos (30 días)', value: segmentCustomers(customers, 'nuevos', now).length },
    { label: 'Inactivos', value: segmentCustomers(customers, 'inactivos', now, paused).length },
  ];

  return (
    <main>
      <PageHeader title="Clientes" description="Quiénes han reservado contigo." />
      {customersQuery.isLoading ? <LoadingState label="Cargando clientes…" /> : null}
      {customersQuery.isError ? <>
        <ErrorBanner message={errorMessage(customersQuery.error)} />
        {!customersQuery.isFetchNextPageError ? <button type="button" className="btn small secondary" disabled={customersQuery.isFetching} onClick={() => void customersQuery.refetch()}>Reintentar clientes</button> : null}
      </> : null}
      {!customersQuery.isLoading && customers.length === 0 && !customersQuery.isError ? (
        <EmptyState
          title="Todavía no tienes clientes."
          description="Cuando alguien reserve contigo por Dale, lo ves aquí con sus visitas."
        />
      ) : null}

      {customers.length > 0 ? (
        <>
          {customersQuery.hasNextPage ? <p className="notice">El resumen y la búsqueda incluyen solo los clientes cargados.</p> : null}
          <section aria-label="Resumen de clientes" className="section">
            <div className="pnl-figures customer-figures">
              {figures.map((figure) => (
                <div key={figure.label} className="pnl-figure">
                  <p className="label">{figure.label}</p>
                  <p className="value">{figure.value}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="section client-toolbar" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
            <input
              type="search"
              className="field-input"
              placeholder="Nombre o teléfono"
              aria-label="Buscar cliente"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="client-toolbar-row">
              <div className="chip-row" style={{ maxWidth: '100%' }} role="group" aria-label="Filtrar clientes">
                {segments.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className="chip"
                    data-status={segment === entry.id ? 'active' : undefined}
                    onClick={() => setSegment(entry.id)}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>
              <div className="client-selects">
                {graded ? (
                  <select
                    className="field-input client-sort"
                    aria-label="Filtrar por nivel"
                    value={levelFilter}
                    onChange={(event) => setLevelFilter(event.target.value as LevelFilter)}
                  >
                    {LEVEL_FILTERS.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                ) : null}
                <select
                  className="field-input client-sort"
                  aria-label="Ordenar clientes"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as CustomerSort)}
                >
                  <option value="reciente">Visita reciente</option>
                  <option value="nombre">Nombre</option>
                  <option value="visitas">Más visitas</option>
                </select>
              </div>
            </div>
          </section>

          <section className="section">
            <div className="client-list-head">
              <h2 className="section-label">
                {visible.length === 1 ? '1 cliente' : `${visible.length} clientes`}
              </h2>
              <div className="seg mini client-view" role="group" aria-label="Ver clientes como">
                {CLIENT_VIEWS.map((option) => (
                  <button key={option.id} type="button" aria-pressed={view === option.id} onClick={() => setView(option.id)}>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            {visible.length === 0 ? (
              <p className="row-sub">
                {levelFilter !== 'todos' && graded
                  ? 'Nadie con ese nivel en este filtro.'
                  : SEGMENT_EMPTY_COPY[segment]}
              </p>
            ) : (
              view === 'tarjetas' ? (
                <ul className="client-cards stagger">
                  {visible.map((customer) => (
                    <ClientCard
                      key={customer.customerId}
                      customer={customer}
                      now={now}
                      profile={profiles.get(customer.customerId)}
                      paused={paused.has(customer.customerId)}
                      graded={graded}
                      onOpen={() => setSelected(customer)}
                    />
                  ))}
                </ul>
              ) : (
              <ul className="rows client-list stagger">
                {visible.map((customer) => (
                  <ClientRow
                    key={customer.customerId}
                    customer={customer}
                    now={now}
                    profile={profiles.get(customer.customerId)}
                    paused={paused.has(customer.customerId)}
                    graded={graded}
                    onOpen={() => setSelected(customer)}
                  />
                ))}
              </ul>
              )
            )}
          </section>

          {customersQuery.hasNextPage && (pages >= MAX_AUTO_PAGES || customersQuery.isFetchNextPageError) ? (
            <button
              type="button"
              className="btn small secondary"
              onClick={() => void customersQuery.fetchNextPage()}
              disabled={customersQuery.isFetchingNextPage}
            >
              {customersQuery.isFetchingNextPage ? 'Cargando…' : customersQuery.isFetchNextPageError ? 'Reintentar cargar más' : 'Cargar más'}
            </button>
          ) : null}
        </>
      ) : null}

      <CustomerDrawer customer={selected} onClose={() => setSelected(null)} />
    </main>
  );
}

type ClientRowProps = {
  customer: BusinessCustomerSummary;
  now: Date;
  profile: CustomerProfile | undefined;
  /** Paused today (a trip): reads En pausa instead of Inactivo. */
  paused: boolean;
  graded: boolean;
  onOpen: () => void;
};

function ClientRow({ customer, now, profile, paused, graded, onOpen }: ClientRowProps) {
  const status = customerStatus(customer, now, paused);
  const visits = customer.totalVisits === 1 ? '1 visita' : `${customer.totalVisits} visitas`;
  const level = graded && profile?.level ? LEVEL_LABEL[profile.level] : null;
  return (
    <li>
      <button type="button" className="client-row" onClick={onOpen}>
        <span aria-hidden="true" className="client-avatar">
          {initialOf(customer.displayName)}
        </span>
        <span className="grow">
          <span className="row-title">
            {customer.displayName}
            {level ? <span className="client-level-word">{level}</span> : null}
          </span>
          <span className="row-sub">
            {visits} · cliente desde {monthYear(customer.firstVisitAt)}
          </span>
        </span>
        <span className="client-end">
          {customer.maskedPhone ? <span className="row-sub client-phone">{customer.maskedPhone}</span> : null}
          <span className="row-sub">última visita {relativeVisit(customer.lastVisitAt, now)}</span>
          {status ? (
            <span className="status" data-tone={status.tone}>
              {status.label}
            </span>
          ) : (
            <span className="status" aria-hidden="true" />
          )}
        </span>
      </button>
    </li>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar los clientes.';
}
